/**
 * Zarvia Search Modal
 * —————————————————————————————————————————————
 * Floating card dialog with predictive search,
 * recently viewed products, and keyboard navigation.
 */
(function () {
  'use strict';

  /* ═══════════════════════════════════════════
     Recently Viewed Products (localStorage)
     ═══════════════════════════════════════════ */
  var STORAGE_KEY = 'zarviaRecentlyViewed';
  var MAX_RECENT = 4;

  var RecentlyViewed = {
    getIds: function () {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      } catch (e) {
        return [];
      }
    },
    addId: function (id) {
      var ids = this.getIds().filter(function (i) { return i !== id; });
      ids.unshift(id);
      if (ids.length > MAX_RECENT) ids = ids.slice(0, MAX_RECENT);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    },
    clear: function () {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  /* Track the current product on PDP */
  var productMeta = document.querySelector('[data-product-id]');
  if (productMeta) {
    RecentlyViewed.addId(String(productMeta.getAttribute('data-product-id')));
  }

  /* ═══════════════════════════════════════════
     DOM refs
     ═══════════════════════════════════════════ */
  var dialog = document.getElementById('zr-search-dialog');
  if (!dialog) return;

  var input    = dialog.querySelector('.zr-search__input');
  var body     = dialog.querySelector('.zr-search__body');
  var clearBtn = dialog.querySelector('[data-zr-search-clear]');
  var closeBtn = dialog.querySelector('[data-zr-search-close]');
  var form     = dialog.querySelector('.zr-search__form');
  var card     = dialog.querySelector('.zr-search__card');

  var abortCtrl    = null;
  var debounceId   = null;
  var currentTerm  = '';
  var navIndex     = -1;

  /* Save server-rendered trending products as default content */
  var defaultContent = body.innerHTML;

  /* ═══════════════════════════════════════════
     Open / Close
     ═══════════════════════════════════════════ */
  function openSearch() {
    dialog.showModal();
    document.body.classList.add('zr-search-open');
    input.value = '';
    clearBtn.hidden = true;
    currentTerm = '';
    navIndex = -1;

    /* Small delay so iOS focuses correctly after dialog animation */
    setTimeout(function () { input.focus(); }, 80);

    loadEmptyState();
  }

  function closeSearch() {
    dialog.close();
    document.body.classList.remove('zr-search-open');
    input.value = '';
    currentTerm = '';
    navIndex = -1;
    if (abortCtrl) { abortCtrl.abort(); abortCtrl = null; }
  }

  /* Trigger buttons (can be multiple) */
  document.querySelectorAll('[data-zr-search-open]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      openSearch();
    });
  });

  /* CMD/Ctrl + K shortcut */
  document.addEventListener('keydown', function (e) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      if (dialog.open) { closeSearch(); } else { openSearch(); }
    }
  });

  closeBtn.addEventListener('click', closeSearch);

  clearBtn.addEventListener('click', function () {
    input.value = '';
    input.focus();
    clearBtn.hidden = true;
    currentTerm = '';
    navIndex = -1;
    loadEmptyState();
  });

  /* Click outside card → close */
  dialog.addEventListener('click', function (e) {
    if (e.target === dialog) closeSearch();
  });

  /* Escape → close */
  dialog.addEventListener('cancel', function (e) {
    e.preventDefault();
    closeSearch();
  });

  /* Prevent empty submit */
  form.addEventListener('submit', function (e) {
    if (!input.value.trim()) e.preventDefault();
  });

  /* ═══════════════════════════════════════════
     Predictive Search
     ═══════════════════════════════════════════ */
  input.addEventListener('input', function () {
    var term = input.value.trim();
    clearBtn.hidden = term.length === 0;
    navIndex = -1;

    clearTimeout(debounceId);

    if (!term) {
      loadEmptyState();
      return;
    }

    debounceId = setTimeout(function () {
      fetchSearchResults(term);
    }, 250);
  });

  function fetchSearchResults(term) {
    if (abortCtrl) abortCtrl.abort();
    abortCtrl = new AbortController();
    currentTerm = term;

    var url = routes.predictive_search_url
      + '?q=' + encodeURIComponent(term)
      + '&section_id=predictive-search'
      + '&resources[limit]=8'
      + '&resources[limit_scope]=each';

    fetch(url, { signal: abortCtrl.signal })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        if (term !== currentTerm) return;           /* stale response */
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el  = doc.getElementById('predictive-search-results');
        if (el) {
          body.innerHTML = el.innerHTML;
          bindResultEvents();
        }
      })
      .catch(function (err) {
        if (err.name !== 'AbortError') console.error('[Zarvia search]', err);
      });
  }

  /* ═══════════════════════════════════════════
     Empty State — Recently Viewed + Trending
     ═══════════════════════════════════════════ */
  function loadEmptyState() {
    if (abortCtrl) abortCtrl.abort();
    currentTerm = '';

    /* Always restore server-rendered trending products */
    body.innerHTML = defaultContent;

    var ids = RecentlyViewed.getIds();
    if (!ids.length) {
      bindResultEvents();
      return;
    }

    /* Fetch recently viewed and prepend above trending */
    abortCtrl = new AbortController();
    var query = ids.map(function (id) { return 'id:' + id; }).join(' OR ');
    var url = (routes.search_url || '/search')
      + '?q=' + encodeURIComponent(query)
      + '&resources[type]=product'
      + '&section_id=predictive-search';

    fetch(url, { signal: abortCtrl.signal })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        if (currentTerm !== '') return;              /* user started typing */
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el  = doc.getElementById('predictive-search-results');
        if (el && el.innerHTML.trim()) {
          body.insertAdjacentHTML('afterbegin', el.innerHTML);
        }
        bindResultEvents();
      })
      .catch(function (err) {
        if (err.name !== 'AbortError') console.error('[Zarvia search]', err);
      });
  }

  /* ═══════════════════════════════════════════
     Dynamic Event Binding
     ═══════════════════════════════════════════ */
  function bindResultEvents() {
    /* Clear recently viewed */
    var rvClear = body.querySelector('[data-zr-clear-rv]');
    if (rvClear) {
      rvClear.addEventListener('click', function (e) {
        e.preventDefault();
        RecentlyViewed.clear();
        var wrapper = body.querySelector('[data-zr-recently-viewed]');
        if (wrapper) {
          wrapper.style.transition = 'opacity .25s ease, transform .25s ease';
          wrapper.style.opacity = '0';
          wrapper.style.transform = 'translateY(-8px)';
          setTimeout(function () {
            wrapper.remove();
          }, 280);
        }
      });
    }
  }

  /* ═══════════════════════════════════════════
     Keyboard Navigation
     ═══════════════════════════════════════════ */
  input.addEventListener('keydown', function (e) {
    var items = body.querySelectorAll('[data-zr-search-item]');
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      navIndex = navIndex < items.length - 1 ? navIndex + 1 : 0;
      highlightItem(items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      navIndex = navIndex > 0 ? navIndex - 1 : items.length - 1;
      highlightItem(items);
    } else if (e.key === 'Enter' && navIndex >= 0 && items[navIndex]) {
      e.preventDefault();
      items[navIndex].click();
    }
  });

  function highlightItem(items) {
    items.forEach(function (el, i) {
      if (i === navIndex) {
        el.setAttribute('aria-selected', 'true');
        el.classList.add('zr-search__item--focused');
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        el.removeAttribute('aria-selected');
        el.classList.remove('zr-search__item--focused');
      }
    });
    input.focus();
  }

  /* Reset nav index on new input */
  input.addEventListener('input', function () { navIndex = -1; });

  /* ═══════════════════════════════════════════
     Integration with StickyHeader
     ═══════════════════════════════════════════ */
  /* Dawn's StickyHeader calls this.searchModal.close(false).
     The old details-modal had a close() method.  We expose a
     shim so StickyHeader doesn't throw an error. */
  var trigger = document.querySelector('[data-zr-search-open]');
  if (trigger) {
    trigger.close = function () { closeSearch(); };
  }

})();
